import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, Lock, Users, Mail, Globe, Plus, Trash2, 
  Key, Eye, EyeOff, AlertTriangle, CheckCircle, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SecurityPage() {
  const [settings, setSettings] = useState(null);
  const [whitelist, setWhitelist] = useState({ emails: [], domains: [], enabled: true });
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newAccessCode, setNewAccessCode] = useState('');
  const [showCode, setShowCode] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsRes, whitelistRes] = await Promise.all([
        fetch(`${API_URL}/api/security/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/security/whitelist`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (settingsRes.ok) {
        setSettings(await settingsRes.json());
      }
      if (whitelistRes.ok) {
        setWhitelist(await whitelistRes.json());
      }
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRegistration = async (enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/security/toggle-registration?enabled=${enabled}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSettings({ ...settings, registration_enabled: enabled });
        toast.success(enabled ? 'Inscriptions publiques activées' : 'Inscriptions publiques désactivées');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleWhitelist = async (enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/security/toggle-whitelist?enabled=${enabled}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSettings({ ...settings, whitelist_enabled: enabled });
        setWhitelist({ ...whitelist, enabled });
        toast.success(enabled ? 'Liste blanche activée' : 'Liste blanche désactivée');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addToWhitelist = async (type) => {
    const value = type === 'email' ? newEmail : newDomain;
    if (!value) return;

    try {
      const res = await fetch(`${API_URL}/api/security/whitelist/add`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(type === 'email' ? { email: value } : { domain: value })
      });

      if (res.ok) {
        toast.success(`${type === 'email' ? 'Email' : 'Domaine'} ajouté`);
        if (type === 'email') {
          setWhitelist({ ...whitelist, emails: [...whitelist.emails, value.toLowerCase()] });
          setNewEmail('');
        } else {
          const domain = value.startsWith('@') ? value : '@' + value;
          setWhitelist({ ...whitelist, domains: [...whitelist.domains, domain.toLowerCase()] });
          setNewDomain('');
        }
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const removeFromWhitelist = async (type, value) => {
    try {
      const res = await fetch(`${API_URL}/api/security/whitelist/remove`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(type === 'email' ? { email: value } : { domain: value })
      });

      if (res.ok) {
        toast.success('Entrée supprimée');
        if (type === 'email') {
          setWhitelist({ ...whitelist, emails: whitelist.emails.filter(e => e !== value) });
        } else {
          setWhitelist({ ...whitelist, domains: whitelist.domains.filter(d => d !== value) });
        }
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const updateAccessCode = async (enabled) => {
    try {
      const res = await fetch(`${API_URL}/api/security/access-code`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          enabled, 
          code: enabled ? newAccessCode : null 
        })
      });

      if (res.ok) {
        setSettings({ ...settings, access_code_enabled: enabled });
        toast.success(enabled ? 'Code d\'accès activé' : 'Code d\'accès désactivé');
        if (!enabled) setNewAccessCode('');
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="security-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            Sécurité & Contrôle d'accès
          </h1>
          <p className="text-muted-foreground">
            Gérez les inscriptions, la liste blanche et les codes d'accès
          </p>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={settings?.registration_enabled ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className={`w-5 h-5 ${settings?.registration_enabled ? 'text-green-600' : 'text-red-600'}`} />
                <span className="font-medium">Inscriptions publiques</span>
              </div>
              <Badge className={settings?.registration_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                {settings?.registration_enabled ? 'Ouvertes' : 'Fermées'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className={whitelist.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className={`w-5 h-5 ${whitelist.enabled ? 'text-blue-600' : 'text-gray-600'}`} />
                <span className="font-medium">Liste blanche</span>
              </div>
              <Badge className={whitelist.enabled ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}>
                {whitelist.enabled ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {whitelist.emails.length} emails, {whitelist.domains.length} domaines
            </p>
          </CardContent>
        </Card>

        <Card className={settings?.access_code_enabled ? 'border-amber-200 bg-amber-50' : 'border-gray-200'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className={`w-5 h-5 ${settings?.access_code_enabled ? 'text-amber-600' : 'text-gray-600'}`} />
                <span className="font-medium">Code d'accès</span>
              </div>
              <Badge className={settings?.access_code_enabled ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}>
                {settings?.access_code_enabled ? 'Requis' : 'Désactivé'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Settings */}
      <Tabs defaultValue="registration" className="space-y-4">
        <TabsList>
          <TabsTrigger value="registration">Inscriptions</TabsTrigger>
          <TabsTrigger value="whitelist">Liste blanche</TabsTrigger>
          <TabsTrigger value="access-code">Code d'accès</TabsTrigger>
        </TabsList>

        {/* Inscriptions */}
        <TabsContent value="registration">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Inscriptions publiques
              </CardTitle>
              <CardDescription>
                Contrôlez qui peut créer un compte sur la plateforme
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <p className="font-medium">Autoriser les inscriptions publiques</p>
                  <p className="text-sm text-muted-foreground">
                    {settings?.registration_enabled 
                      ? "N'importe qui peut créer un compte (selon la liste blanche)"
                      : "Seul l'administrateur peut créer des comptes"}
                  </p>
                </div>
                <Switch
                  checked={settings?.registration_enabled}
                  onCheckedChange={toggleRegistration}
                />
              </div>

              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Recommandation sécurité</p>
                    <p className="text-sm text-amber-700">
                      Pour un maximum de sécurité, désactivez les inscriptions publiques et créez les comptes manuellement via la section "Prospection".
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Liste blanche */}
        <TabsContent value="whitelist">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Liste blanche
              </CardTitle>
              <CardDescription>
                Définissez quels emails ou domaines sont autorisés à s'inscrire
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <p className="font-medium">Activer la liste blanche</p>
                  <p className="text-sm text-muted-foreground">
                    Seuls les emails/domaines listés pourront s'inscrire
                  </p>
                </div>
                <Switch
                  checked={whitelist.enabled}
                  onCheckedChange={toggleWhitelist}
                />
              </div>

              {/* Ajouter email */}
              <div className="space-y-2">
                <Label>Ajouter un email</Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="utilisateur@exemple.fr"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                  />
                  <Button onClick={() => addToWhitelist('email')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
              </div>

              {/* Liste des emails */}
              {whitelist.emails.length > 0 && (
                <div className="space-y-2">
                  <Label>Emails autorisés ({whitelist.emails.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {whitelist.emails.map((email) => (
                      <Badge key={email} variant="secondary" className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {email}
                        <button
                          onClick={() => removeFromWhitelist('email', email)}
                          className="ml-1 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Ajouter domaine */}
              <div className="space-y-2">
                <Label>Ajouter un domaine entier</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="@entreprise.fr"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                  <Button onClick={() => addToWhitelist('domain')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Tous les emails de ce domaine seront autorisés (ex: @maison-casia.com)
                </p>
              </div>

              {/* Liste des domaines */}
              {whitelist.domains.length > 0 && (
                <div className="space-y-2">
                  <Label>Domaines autorisés ({whitelist.domains.length})</Label>
                  <div className="flex flex-wrap gap-2">
                    {whitelist.domains.map((domain) => (
                      <Badge key={domain} variant="outline" className="flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {domain}
                        <button
                          onClick={() => removeFromWhitelist('domain', domain)}
                          className="ml-1 hover:text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Code d'accès */}
        <TabsContent value="access-code">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Code d'accès
              </CardTitle>
              <CardDescription>
                Ajoutez une couche de sécurité supplémentaire avec un code d'accès
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border">
                <div>
                  <p className="font-medium">Activer le code d'accès</p>
                  <p className="text-sm text-muted-foreground">
                    Les utilisateurs devront entrer un code pour s'inscrire
                  </p>
                </div>
                <Switch
                  checked={settings?.access_code_enabled}
                  onCheckedChange={(checked) => {
                    if (checked && !newAccessCode) {
                      toast.error('Définissez d\'abord un code d\'accès');
                      return;
                    }
                    updateAccessCode(checked);
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label>Définir le code d'accès</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      type={showCode ? 'text' : 'password'}
                      placeholder="Entrez un code secret"
                      value={newAccessCode}
                      onChange={(e) => setNewAccessCode(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCode(!showCode)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showCode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button 
                    onClick={() => updateAccessCode(true)}
                    disabled={!newAccessCode}
                  >
                    Enregistrer
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Partagez ce code uniquement avec les personnes autorisées
                </p>
              </div>

              {settings?.access_code && (
                <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <p className="text-green-800">
                      Code actuel : <code className="bg-green-100 px-2 py-0.5 rounded">{settings.access_code}</code>
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
